import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient, type QueryState } from '@tanstack/react-query'
import { api, ErroDaApi } from '../api/client'
import { chaves } from '../api/queries/chaves'
import {
  EVENTO_SESSAO_ENCERRADA,
  EVENTO_SESSAO_INICIADA,
  encerrarSessao,
  espelharSessaoDeOutrasAbas,
  tokenDeAcesso,
} from './armazenamento'
import { ContextoDaSessao, erroDeLoginInicial, type EstadoDaSessao } from './contexto'
import type { UsuarioLogado } from '../types/gmud'

/**
 * Mantém quem está logado, lido do banco a cada carga.
 *
 * Os papéis vêm de `GET /me` e não do token: revogar um papel aparece na tela no
 * próximo carregamento, sem esperar o token expirar. O provedor não guarda permissão
 * derivada nem calcula nada a partir dela — é transporte.
 *
 * A sessão aparece e some sem recarregar a página, e por isso o provedor escuta os dois
 * eventos do armazenamento: entrar por usuário e senha grava o token no meio da vida da
 * aplicação, e a renovação recusada o apaga do mesmo jeito. Ler o token só na montagem
 * deixava a metade "entrou" sem ninguém escutando.
 *
 * @param props filhos da árvore
 * @returns a árvore embrulhada no contexto de sessão
 */
export function ProvedorDeSessao({ children }: { children: ReactNode }) {
  const cliente = useQueryClient()
  const [temToken, setTemToken] = useState(() => tokenDeAcesso() !== null)
  const [erroDeLogin, setErroDeLogin] = useState<string | null>(erroDeLoginInicial)

  useEffect(() => {
    const aoEncerrar = () => {
      setTemToken(false)
      // O cache guarda dados de quem saiu. Limpar evita que a próxima pessoa a
      // entrar neste navegador veja, por um instante, a lista da anterior.
      cliente.clear()
    }
    window.addEventListener(EVENTO_SESSAO_ENCERRADA, aoEncerrar)
    return () => window.removeEventListener(EVENTO_SESSAO_ENCERRADA, aoEncerrar)
  }, [cliente])

  useEffect(() => {
    const aoIniciar = () => {
      setTemToken(true)
      // Ligar `temToken` basta quando não havia sessão: a consulta estava desligada e sobe
      // buscando. Não basta quando o `/me` anterior FALHOU — a consulta já está ligada, o
      // erro guardado não se refaz sozinho, e quem acabou de acertar a senha ficaria olhando
      // a tela de login sem uma linha de explicação.
      //
      // O 401 fica de fora, e é ele que impede um laço: `gravarSessao` também é chamada pela
      // RENOVAÇÃO, então um servidor que emite um token e recusa esse mesmo token (duas
      // instâncias com chaves de assinatura diferentes, por exemplo) giraria refaz → 401 →
      // renova → refaz sem parar. Recusa de credencial é resposta definitiva; insistir nela
      // não muda o resultado, só multiplica as chamadas.
      if (falhouPorMotivoQuePodeMudar(cliente.getQueryState(chaves.eu()))) {
        void cliente.invalidateQueries({ queryKey: chaves.eu() })
      }
    }
    window.addEventListener(EVENTO_SESSAO_INICIADA, aoIniciar)
    return () => window.removeEventListener(EVENTO_SESSAO_INICIADA, aoIniciar)
  }, [cliente])

  // Login e logout feitos em OUTRA aba chegam aqui pelos mesmos dois eventos: sem isso, sair
  // numa aba deixava a outra com o cache de quem saiu e sem caminho de volta ao login.
  useEffect(() => espelharSessaoDeOutrasAbas(), [])

  const { data: usuario, isPending } = useQuery({
    queryKey: chaves.eu(),
    queryFn: async () => (await api.get<UsuarioLogado>('/me')).data,
    enabled: temToken,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const sair = useCallback(() => {
    encerrarSessao()
  }, [])

  const descartarErroDeLogin = useCallback(() => setErroDeLogin(null), [])

  const valor = useMemo<EstadoDaSessao>(
    () => ({
      usuario: usuario ?? null,
      carregando: temToken && isPending,
      autenticado: Boolean(usuario),
      erroDeLogin,
      sair,
      descartarErroDeLogin,
    }),
    [usuario, temToken, isPending, erroDeLogin, sair, descartarErroDeLogin],
  )

  return <ContextoDaSessao.Provider value={valor}>{children}</ContextoDaSessao.Provider>
}

/**
 * @param estado o que o cache guarda sobre o `GET /me`
 * @returns verdadeiro quando ele falhou por algo que uma nova tentativa pode resolver
 */
function falhouPorMotivoQuePodeMudar(estado: QueryState<unknown, unknown> | undefined): boolean {
  if (estado?.status !== 'error') {
    return false
  }
  return !(estado.error instanceof ErroDaApi) || estado.error.status !== 401
}
